/**
 * Full-screen photo viewer for the listing page, built on PhotoSwipe 5.
 *
 * PhotoSwipe and its stylesheet are loaded with a dynamic import on the
 * first tap, so neither is in the initial bundle: a visitor who never opens
 * the viewer never downloads it.
 *
 * The browser's back button closes the viewer. Opening it adds one history
 * entry with the same URL and router state; the pops that belong to the
 * viewer are taken through util/popStateGuard, so React Router never sees
 * them (a location change there would re-run the listing page's loadData
 * and scroll the page to the top).
 */

import { setupPopStateGuard, setPopStateHandler } from '../../../util/popStateGuard';

// Each slide in the viewer uses the largest scaled variant (1800×2400 for a
// 3:4 photo); the rest are fallbacks for an image that lacks it.
export const VIEWER_VARIANTS = ['scaled-xlarge', 'scaled-large', 'scaled-medium', 'scaled-small'];
// Shown while the large photo loads: the size the gallery has most likely
// loaded already, so it comes from the browser cache.
const PLACEHOLDER_VARIANTS = ['scaled-large', 'scaled-medium', 'scaled-small'];

const loadPhotoSwipe = () =>
  Promise.all([
    import(/* webpackChunkName: "PhotoSwipe" */ 'photoswipe'),
    import(/* webpackChunkName: "PhotoSwipe" */ 'photoswipe/style.css'),
  ]).then(([photoSwipeModule]) => photoSwipeModule.default);

const firstVariant = (variants, names) => names.map(name => variants[name]).find(v => v?.url);

/**
 * PhotoSwipe slides from listing images. An image without any scaled
 * variant (or without its size) gets no slide data, which PhotoSwipe
 * shows as its error message.
 *
 * @param {Array<Object>} images listing image entities
 * @param {Array<string>} alts alt text for each image, in the same order
 * @returns {Array<Object>} PhotoSwipe data source
 */
export const toViewerSlides = (images, alts) =>
  images.map((image, i) => {
    const variants = image?.attributes?.variants || {};
    const large = firstVariant(variants, VIEWER_VARIANTS);
    const placeholder = firstVariant(variants, PLACEHOLDER_VARIANTS);
    if (!large?.width || !large?.height) {
      return { alt: alts[i] };
    }
    return {
      src: large.url,
      width: large.width,
      height: large.height,
      msrc: placeholder?.url,
      alt: alts[i],
    };
  });

// Marks the history entry the viewer adds.
const HISTORY_MARKER = 'zuprizePhotoViewer';
// Closes the open viewer while its history entry is the current one.
let closeOpenViewer = null;
// The next popstate is one the viewer caused itself with history.back().
let swallowNextPop = false;

// Returns true for the pops that belong to the viewer, which the router
// then doesn't see.
const onPopState = e => {
  if (closeOpenViewer) {
    // Back button while the viewer is open.
    const close = closeOpenViewer;
    closeOpenViewer = null;
    close();
    return true;
  } else if (swallowNextPop) {
    swallowNextPop = false;
    return true;
  } else if (e.state?.[HISTORY_MARKER]) {
    // Forward button onto the entry of a viewer that has closed: step back
    // to the listing's own entry, unseen by the router.
    swallowNextPop = true;
    window.history.back();
    return true;
  }
  return false;
};

/**
 * Close the viewer with the browser's back button.
 *
 * @param {Object} pswp PhotoSwipe instance, before init()
 */
const closeWithBackButton = pswp => {
  // Normally already set up by src/index.js, before the router.
  setupPopStateGuard();
  setPopStateHandler(onPopState);
  window.history.pushState({ ...window.history.state, [HISTORY_MARKER]: true }, '');

  const close = () => {
    // PhotoSwipe ignores close() during its opening fade.
    if (pswp.opener?.isOpen) {
      pswp.close();
    } else {
      pswp.on('openingAnimationEnd', () => pswp.close());
    }
  };
  closeOpenViewer = close;

  pswp.on('destroy', () => {
    if (closeOpenViewer === close) {
      // Closed with the X, a swipe down or Esc: drop the viewer's entry so the
      // next back goes where the visitor expects.
      closeOpenViewer = null;
      swallowNextPop = true;
      window.history.back();
    }
  });
};

/**
 * Open the full-screen viewer.
 *
 * @param {Object} params
 * @param {Array<Object>} params.slides from toViewerSlides
 * @param {number} params.index the slide to open
 * @param {string} params.closeTitle label for the close button
 * @param {string} params.prevTitle label for the previous-photo arrow
 * @param {string} params.nextTitle label for the next-photo arrow
 * @param {string} params.errorMsg shown when a photo can't be loaded
 * @param {Function} [params.onClose] called with the index of the slide shown at close
 * @returns {Promise<Object|null>} the PhotoSwipe instance, or null when the visitor left
 *   the page while PhotoSwipe was loading
 */
export const openPhotoViewer = params => {
  const { slides, index, closeTitle, prevTitle, nextTitle, errorMsg, onClose } = params;
  const hrefAtTap = window.location.href;
  return loadPhotoSwipe().then(PhotoSwipe => {
    if (window.location.href !== hrefAtTap) {
      return null;
    }
    const pswp = new PhotoSwipe({
      dataSource: slides,
      index,
      bgOpacity: 1,
      // A fade: the gallery shows the photo whole inside a frame, so the
      // zoom-from-thumbnail animation would start from the wrong box.
      showHideAnimationType: 'fade',
      // Desktop: click zooms in and out, the scroll wheel zooms.
      wheelToZoom: true,
      imageClickAction: 'zoom',
      // Phones: pinch or double-tap to zoom, swipe down to close.
      tapAction: 'toggle-controls',
      doubleTapAction: 'zoom',
      // Zoom is by click, scroll, pinch or double-tap; no separate button.
      zoom: false,
      loop: false,
      indexIndicatorSep: '/',
      closeTitle,
      errorMsg,
      arrowPrevTitle: prevTitle,
      arrowNextTitle: nextTitle,
    });
    closeWithBackButton(pswp);
    if (onClose) {
      pswp.on('close', () => onClose(pswp.currIndex));
    }
    pswp.init();
    return pswp;
  });
};
